import { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup, Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { io } from "socket.io-client";
import Select from "react-select";
import config from "../../config";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import englishStrings from "../../locales/languages/en.json";
import truckyService from "../../common/services/trucky-service";
import { isPremiumEnabled } from "../../common/utils/PremiumUtils";
import { EnterpriseContext } from "../../components/enterprise/EnterpriseContext";
import CopyButton from "../../components/ui/CopyButton";
import VectorMapViewer from "../components/liveMap/VectorMapViewer";
import FollowedPlayerCard from "../components/liveMap/FollowedPlayerCard";
import LayerControl from "../components/liveMap/LayerControl";
import { loadActiveLayers, saveActiveLayers } from "../components/liveMap/layerVisibility";
import { gameHeadingToMapBearing, gameToMapCoords } from "../components/liveMap/coordinates";
import { createPermalink, readPermalinkCamera } from "../components/liveMap/permalink";
import { getPlayerTooltipProperties } from "../components/liveMap/playerTooltip";
import "./LiveMap.scss";

const MAPS = [
  { code: "ets2", label: "Euro Truck Simulator 2" },
  { code: "ats", label: "American Truck Simulator" },
  { code: "promods", label: "ProMods" },
];

const POI_TYPES = {
  city: { icon: "city", color: "#4dabf7" },
  company: { icon: "building", color: "#ffa94d" },
  country: { icon: "flag", color: "#69db7c" },
};

const formatPlaceOption = (option) => (
  <span className="d-flex align-items-center" style={{ gap: 6 }}>
    <FontAwesomeIcon
      icon={POI_TYPES[option.type].icon}
      style={{ color: POI_TYPES[option.type].color, flexShrink: 0 }}
      fixedWidth
    />
    <span>{option.label}</span>
  </span>
);

const getInitialMap = () => {
  const queryMap = new URLSearchParams(window.location.search).get("game");
  return MAPS.some((map) => map.code === queryMap) ? queryMap : "ets2";
};

const LiveMap = ({ container, user: routeUser }) => {
  const { globalContext } = useContext(EnterpriseContext);
  const user = globalContext?.member || routeUser;
  const [game, setGame] = useState(getInitialMap);
  const [initialCamera, setInitialCamera] = useState(() =>
    readPermalinkCamera(window.location.search)
  );
  const [permalink, setPermalink] = useState("");
  const [mapInfo, setMapInfo] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [socketError, setSocketError] = useState(null);
  const [socketStatus, setSocketStatus] = useState("connecting");
  const [allPlayers, setAllPlayers] = useState({});
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [onlyMyCompany, setOnlyMyCompany] = useState(false);
  const [gameMode, setGameMode] = useState(null);
  const [pointsOfInterest, setPointsOfInterest] = useState([]);
  const [placesRequested, setPlacesRequested] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [activeLayers, setActiveLayers] = useState(() =>
    loadActiveLayers(window.localStorage)
  );

  const hasPremiumAccess = isPremiumEnabled(user);
  const gameModeOptions = [
    { value: "singleplayer", label: container.localeManager.strings.singleplayer || englishStrings.singleplayer },
    { value: "truckersmp", label: "TruckersMP" },
    { value: "haulmp", label: "HaulMP" },
  ];

  useEffect(() => {
    truckyService.setDocumentTitle("Live Map");
  }, []);

  useEffect(() => {
    saveActiveLayers(window.localStorage, activeLayers);
  }, [activeLayers]);

  useEffect(() => {
    if (!mapInstance) return undefined;

    const updatePermalink = () =>
      setPermalink(createPermalink(window.location.href, game, mapInstance));
    updatePermalink();
    mapInstance.on("moveend", updatePermalink);
    return () => mapInstance.off("moveend", updatePermalink);
  }, [mapInstance, game]);

  useEffect(() => {
    let active = true;
    setMapInfo(null);
    setMapError(null);

    const baseUrl =
      config.VECTOR_LIVE_MAP_DATA_BASE_URL ||
      "https://cdn.truckyapp.com/map_data";
    fetch(`${baseUrl}/${game}/VectorTileMapInfo.json?t=${Date.now()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Map data request failed (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        if (active) setMapInfo({ ...data, game });
      })
      .catch((error) => {
        console.error("Unable to load live map data:", error);
        if (active) setMapError(error.message);
      });

    return () => {
      active = false;
    };
  }, [game]);

  useEffect(() => {
    if (!placesRequested) return undefined;

    const baseUrl =
      config.VECTOR_LIVE_MAP_DATA_BASE_URL ||
      "https://cdn.truckyapp.com/map_data";
    let active = true;

    fetch(`${baseUrl}/${game}/Poi.json?t=${Date.now()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Places request failed (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        if (!active) return;

        const countries = Object.fromEntries(
          (data.countries || []).map((country) => [country.id, country])
        );
        const places = [];

        (data.cities || []).forEach((city) => {
          const country = countries[city.countryId];
          places.push({
            type: "city",
            label: country ? `${city.name} (${country.name})` : city.name,
            x: city.position?.x,
            z: city.position?.z,
            zoom: 9.5,
          });
          (city.companies || []).forEach((company) => {
            places.push({
              type: "company",
              label: `${company.name} (${city.name})`,
              x: company.position?.x,
              z: company.position?.z,
              zoom: 12,
            });
          });
        });

        (data.countries || []).forEach((country) => {
          places.push({
            type: "country",
            label: country.name,
            x: country.position?.x,
            z: country.position?.z,
            zoom: 7,
          });
        });

        setPointsOfInterest(places);
      })
      .catch((error) => {
        console.error("Unable to load live map places:", error);
        if (active) setPointsOfInterest([]);
      });

    return () => {
      active = false;
    };
  }, [game, placesRequested]);

  useEffect(() => {
    if (!hasPremiumAccess) {
      setSocketStatus("disconnected");
      setSocketError(null);
      setAllPlayers({});
      setSelectedPlayerId(null);
      return undefined;
    }

    const socket = io(`${config.LIVE_SOCKET_URL}/live-out`, {
      transports: ["websocket"],
      secure: true,
    });
    const pendingPlayers = {};
    const flushInterval = window.setInterval(() => {
      const updates = Object.entries(pendingPlayers);
      if (updates.length === 0) return;

      updates.forEach(([id]) => delete pendingPlayers[id]);
      setAllPlayers((current) => {
        const updated = { ...current };
        updates.forEach(([id, player]) => {
          if (player === null) delete updated[id];
          else updated[id] = player;
        });
        return updated;
      });
    }, 250);

    setSocketStatus("connecting");
    setSocketError(null);
    setAllPlayers({});
    setSelectedPlayerId(null);

    socket.on("connect", () => {
      setSocketStatus("connected");
      setSocketError(null);
      socket.emit("getAllPlayers", { map: game });
    });
    socket.on("disconnect", () => setSocketStatus("disconnected"));
    socket.on("connect_error", (error) => {
      console.error("Live map connection failed:", error);
      setSocketStatus("error");
      setSocketError(error.message);
    });
    socket.on("getAllPlayersResult", (players) => {
      setAllPlayers(players || {});
    });
    socket.on("playerStatusUpdate", (player) => {
      if (!player?.user_id) return;
      pendingPlayers[player.user_id] = player;
    });
    socket.on("playerDisconnected", (player) => {
      if (player?.id == null) return;
      pendingPlayers[player.id] = null;
    });

    return () => {
      window.clearInterval(flushInterval);
      socket.disconnect();
    };
  }, [game, hasPremiumAccess]);

  const visiblePlayers = useMemo(
    () =>
      (hasPremiumAccess ? Object.values(allPlayers) : []).filter(
        (player) =>
          player?.position &&
          (gameMode == null ||
            (gameMode === "singleplayer"
              ? ["sp", "convoy"].includes(player.game_mode)
              : player.game_mode === gameMode)) &&
          (!onlyMyCompany ||
            user?.company_id == null ||
            player.company_id === user.company_id)
      ),
    [allPlayers, onlyMyCompany, user?.company_id, gameMode, hasPremiumAccess]
  );

  useEffect(() => {
    if (
      selectedPlayerId != null &&
      !visiblePlayers.some(
        (player) => Number(player.user_id) === Number(selectedPlayerId)
      )
    ) {
      setSelectedPlayerId(null);
    }
  }, [visiblePlayers, selectedPlayerId]);

  const playerFeatures = useMemo(
    () => ({
      type: "FeatureCollection",
      features: visiblePlayers.map((player) => {
        const position = player.position;
        const coordinates = gameToMapCoords(position.x, position.z, mapInfo);
        const isCurrentUser = Number(player.user_id) === Number(user?.id);
        const isCompanyMember =
          user?.company_id != null && player.company_id === user.company_id;
        const color = isCurrentUser
          ? "#39c6f4"
          : player.attributes?.ambassador
            ? "#a51745"
            : player.attributes?.premium
              ? "#ffb200"
              : isCompanyMember
                ? "#2c9cdb"
                : "#e64646";

        return {
          type: "Feature",
          geometry: { type: "Point", coordinates },
          properties: {
            id: player.user_id,
            user_name: player.user_name || "Trucky user",
            avatar_url: player.avatar_url || "",
            ...getPlayerTooltipProperties(player, user),
            followed: Number(player.user_id) === Number(selectedPlayerId),
            color,
            mapBearing: gameHeadingToMapBearing(
              player.heading,
              position.x,
              position.z,
              mapInfo
            ),
          },
        };
      }),
    }),
    [visiblePlayers, mapInfo, user, selectedPlayerId]
  );

  const selectedPlayer =
    !hasPremiumAccess || selectedPlayerId == null ? null : allPlayers[selectedPlayerId];
  const currentUserPlayer =
    hasPremiumAccess && user?.id != null ? allPlayers[user.id] : null;

  const placeOptions = useMemo(
    () =>
      pointsOfInterest
        .map((place, index) => ({ ...place, value: String(index) }))
        .filter((place) => Number.isFinite(place.x) && Number.isFinite(place.z))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [pointsOfInterest]
  );

  const playerOptions = useMemo(
    () =>
      visiblePlayers
        .slice()
        .sort((a, b) =>
          (a.user_name || "").localeCompare(b.user_name || "")
        ),
    [visiblePlayers]
  );

  const changeGame = (newGame) => {
    if (newGame === game) return;
    setGame(newGame);
    setInitialCamera(null);
    setPermalink("");
    setMapInstance(null);
    setOnlyMyCompany(false);
    setPointsOfInterest([]);
    setPlacesRequested(false);
  };

  const goToPlace = (place) => {
    if (!place || !mapInstance) return;

    setSelectedPlayerId(null);
    mapInstance.flyTo({
      center: gameToMapCoords(place.x, place.z, mapInfo),
      zoom: place.zoom,
      essential: true,
    });
    setMobileToolsOpen(false);
  };

  return (
    <section className="vector-live-map-page">
      {mapError && (
        <Alert color="danger">
          The map could not be loaded: {mapError}
        </Alert>
      )}
      {hasPremiumAccess && socketError && (
        <Alert color="warning">
          The map is available, but live player updates could not connect:{" "}
          {socketError}
        </Alert>
      )}

      <div className="vector-live-map-frame">
        <div className={`vector-live-map-toolbar${hasPremiumAccess ? " vector-live-map-toolbar-full" : ""}${mobileToolsOpen ? " is-open" : ""}`}>
          <Button
            className="vector-live-map-tools-toggle"
            color="secondary"
            size="sm"
            aria-expanded={mobileToolsOpen}
            aria-label={container.localeManager.strings.mapTools || englishStrings.mapTools}
            onClick={() => setMobileToolsOpen((open) => !open)}
          >
            <FontAwesomeIcon icon="filter" className="me-2" />
            {container.localeManager.strings.mapTools || englishStrings.mapTools}
            <FontAwesomeIcon icon={mobileToolsOpen ? "chevron-up" : "chevron-down"} className="ms-2" />
          </Button>
          <ButtonGroup size="sm" className="vector-live-map-game-buttons">
            {MAPS.map((map) => (
              <Button
                key={map.code}
                size="sm"
                color={game === map.code ? "primary" : "secondary"}
                onClick={() => changeGame(map.code)}
              >
                {map.code === "promods" ? "ProMods" : map.code.toUpperCase()}
              </Button>
            ))}
          </ButtonGroup>

          <LayerControl
            activeLayers={activeLayers}
            tooltipStrings={container.localeManager.strings}
            onChange={setActiveLayers}
            showPlayers={hasPremiumAccess}
          />

          {currentUserPlayer?.position && (
            <Button
              className="vector-live-map-follow-me"
              color="primary"
              size="sm"
              title={container.localeManager.strings.followMe || englishStrings.followMe}
              aria-label={container.localeManager.strings.followMe || englishStrings.followMe}
              onClick={() => {
                setGameMode(null);
                setOnlyMyCompany(false);
                setSelectedPlayerId(Number(currentUserPlayer.user_id));
                setMobileToolsOpen(false);
              }}
            >
              <FontAwesomeIcon icon="location-crosshairs" className="mx-0" />
            </Button>
          )}

          {hasPremiumAccess && <div className={`vector-live-map-status is-${socketStatus}`}>
            <span />
            {socketStatus === "connected"
              ? "Live"
              : socketStatus === "connecting"
                ? "Connecting"
                : socketStatus === "error"
                  ? "Connection issue"
                  : "Offline"}
          </div>}

          {permalink && (
            <div className="vector-live-map-permalink">
              <a href={permalink}>
                {container.localeManager.strings.permalink || englishStrings.permalink}
              </a>
              <CopyButton
                text={permalink}
                color="link"
                className="btn-icon p-0"
              />
            </div>
          )}

          <div className="vector-live-map-toolbar-selects">
            {hasPremiumAccess && user?.company_id != null && (
              <label className="vector-live-map-company-filter">
                <Input
                  type="checkbox"
                  checked={onlyMyCompany}
                  onChange={(event) => setOnlyMyCompany(event.target.checked)}
                />
                My company
              </label>
            )}
            {hasPremiumAccess && <Select
              aria-label={container.localeManager.strings.gameMode || englishStrings.gameMode}
              className="vector-live-map-select"
              classNamePrefix="react-select"
              styles={reactSelectCustomStyles}
              menuPortalTarget={document.body}
              placeholder={`${container.localeManager.strings.gameMode || englishStrings.gameMode}: ${container.localeManager.strings.all || englishStrings.all}`}
              options={gameModeOptions}
              value={gameModeOptions.find((option) => option.value === gameMode) || null}
              isClearable
              isSearchable={false}
              onChange={(option) => setGameMode(option?.value || null)}
            />}
            <Select
              aria-label="Find a place"
              className="vector-live-map-select"
              classNamePrefix="react-select"
              styles={reactSelectCustomStyles}
              menuPortalTarget={document.body}
              placeholder={container.localeManager.strings.searchCitiesCompanyCountries || englishStrings.searchCitiesCompanyCountries}
              options={placeOptions}
              formatOptionLabel={formatPlaceOption}
              value={null}
              isSearchable
              onFocus={() => setPlacesRequested(true)}
              onChange={goToPlace}
            />
            {hasPremiumAccess && <Select
              aria-label="Follow a player"
              className="vector-live-map-select"
              classNamePrefix="react-select"
              styles={reactSelectCustomStyles}
              menuPortalTarget={document.body}
              placeholder={container.localeManager.strings.searchForPlayerName || englishStrings.searchForPlayerName}
              options={playerOptions}
              getOptionValue={(player) => String(player.user_id)}
              getOptionLabel={(player) => player.user_name || "Trucky user"}
              value={selectedPlayer || null}
              isClearable
              isSearchable
              onChange={(player) => {
                setSelectedPlayerId(player ? Number(player.user_id) : null);
                if (player) setMobileToolsOpen(false);
              }}
            />}
          </div>
          {!hasPremiumAccess && (
            <div className="vector-live-map-premium-notice">
              <FontAwesomeIcon icon="star" className="me-2" />
              {container.localeManager.strings.liveMapPremiumNotice || englishStrings.liveMapPremiumNotice}
            </div>
          )}
          {hasPremiumAccess && <span className="vector-live-map-player-count">
            <FontAwesomeIcon icon="users" className="me-2" />
            {visiblePlayers.length} online
          </span>}
        </div>

        <div className="vector-live-map-stage">
          {mapInfo && (
            <VectorMapViewer
              key={game}
              game={game}
              initialCamera={initialCamera}
              mapInfo={mapInfo}
              players={playerFeatures}
              followedPlayer={hasPremiumAccess ? selectedPlayerId : null}
              activeLayers={activeLayers}
              onMapLoad={setMapInstance}
              onPlayerClick={setSelectedPlayerId}
              onStopFollowing={() => setSelectedPlayerId(null)}
            />
          )}

          {!mapInfo && !mapError && (
            <div className="vector-live-map-loading">
              <FontAwesomeIcon icon="spinner" spin />
              <span>Loading the map…</span>
            </div>
          )}

          {selectedPlayer && (
            <FollowedPlayerCard
              player={selectedPlayer}
              user={user}
              strings={container.localeManager.strings}
              onClose={() => setSelectedPlayerId(null)}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default LiveMap;
